import { defineStore } from "pinia"

export const usePreferencesStore = defineStore("preferences", {
  state: () => ({
    userPreferences: null,
    exchangeRates: [],
    isLoading: false,
    error: null,
  }),

  getters: {
    preferredCurrency: (state) => state.userPreferences?.preferred_currency || "USD",
    preferredTimezone: (state) => state.userPreferences?.timezone || "UTC",
    theme: (state) => state.userPreferences?.theme || "light",
    dateFormat: (state) => state.userPreferences?.date_format || "YYYY-MM-DD",
    timeFormat: (state) => state.userPreferences?.time_format || "24h",
    language: (state) => state.userPreferences?.language || "en",
    dashboardLayout: (state) => state.userPreferences?.dashboard_layout || "default",
    notificationsEnabled: (state) => state.userPreferences?.notifications_enabled !== false,

    /**
     * Get exchange rate for a currency pair
     */
    getExchangeRate: (state) => (from, to) => {
      if (from === to) return 1.0

      // Find the rate in the array
      const rate = state.exchangeRates.find((r) => r.currency_from === from && r.currency_to === to)
      if (rate) return parseFloat(rate.rate)

      // Try inverse rate
      const inverseRate = state.exchangeRates.find((r) => r.currency_from === to && r.currency_to === from)
      if (inverseRate) return 1 / parseFloat(inverseRate.rate)

      return 1.0
    },
  },

  actions: {
    getAxios() {
      return this.$axios || useNuxtApp().$axios
    },
    /**
     * Fetch user preferences
     */
    async fetchPreferences() {
      this.isLoading = true
      this.error = null
      try {
        const response = await this.getAxios().get("/api/v1/preferences")
        this.userPreferences = response.data.data
        return response.data.data
      } catch (error) {
        this.error = error.response?.data?.message || "Failed to fetch preferences"
        console.error("Error fetching preferences:", error)

        // Set default preferences if fetch fails
        this.userPreferences = {
          preferred_currency: "USD",
          timezone: "UTC",
          date_format: "YYYY-MM-DD",
          time_format: "24h",
          theme: "light",
          dashboard_layout: "default",
          notifications_enabled: true,
          language: "en",
        }
      } finally {
        this.isLoading = false
      }
    },

    /**
     * Update user preferences
     */
    async updatePreferences(preferences) {
      this.isLoading = true
      this.error = null
      try {
        const response = await this.getAxios().put("/api/v1/preferences", preferences)
        this.userPreferences = response.data.data
        return response.data
      } catch (error) {
        this.error = error.response?.data?.message || "Failed to update preferences"
        console.error("Error updating preferences:", error)
        throw error
      } finally {
        this.isLoading = false
      }
    },

    /**
     * Fetch exchange rates
     */
    async fetchExchangeRates() {
      this.isLoading = true
      this.error = null
      try {
        const response = await this.getAxios().get("/api/v1/exchange-rates")

        // Store as array for admin display
        this.exchangeRates = response.data.data || []
        return this.exchangeRates
      } catch (error) {
        this.error = error.response?.data?.message || "Failed to fetch exchange rates"
        console.error("Error fetching exchange rates:", error)

        // Set default rates for common currencies (fallback)
        this.exchangeRates = [
          { currency_from: "USD", currency_to: "TZS", rate: 2500, source: "manual", last_updated: new Date() },
          { currency_from: "USD", currency_to: "KES", rate: 150, source: "manual", last_updated: new Date() },
          { currency_from: "USD", currency_to: "UGX", rate: 3700, source: "manual", last_updated: new Date() },
          { currency_from: "USD", currency_to: "EUR", rate: 0.92, source: "manual", last_updated: new Date() },
          { currency_from: "USD", currency_to: "GBP", rate: 0.79, source: "manual", last_updated: new Date() },
        ]
      } finally {
        this.isLoading = false
      }
    },

    /**
     * Update currency preference
     */
    async setCurrency(currency) {
      await this.updatePreferences({ preferred_currency: currency })
    },

    /**
     * Update theme preference
     */
    async setTheme(theme) {
      await this.updatePreferences({ theme })

      // Apply theme immediately
      if (process.client) {
        if (theme === "dark") {
          document.documentElement.classList.add("dark")
        } else if (theme === "light") {
          document.documentElement.classList.remove("dark")
        }
      }
    },

    /**
     * Update timezone preference
     */
    async setTimezone(timezone) {
      await this.updatePreferences({ timezone })
    },

    /**
     * Update exchange rates from OpenExchangeRates API (Admin only)
     */
    async updateLiveExchangeRates() {
      this.isLoading = true
      this.error = null
      try {
        // Using this.$axios injected by pinia plugin
        const response = await this.getAxios().post("/api/v1/admin/exchange-rates/update")

        if (response.data.success) {
          // Refresh exchange rates after update
          await this.fetchExchangeRates()
          return response.data
        }

        throw new Error(response.data.message || "Failed to update exchange rates")
      } catch (error) {
        this.error = error.response?.data?.message || error.message || "Failed to update exchange rates"
        console.error("Error updating live exchange rates:", error)
        throw error
      } finally {
        this.isLoading = false
      }
    },

    /**
     * Set exchange rate manually (Admin only)
     */
    async setManualExchangeRate(data) {
      this.isLoading = true
      this.error = null
      try {
        // Using this.$axios injected by pinia plugin
        const response = await this.getAxios().post("/api/v1/admin/exchange-rates/set", data)

        if (response.data.success) {
          // Refresh exchange rates after setting manual rate
          await this.fetchExchangeRates()
          return response.data
        }

        throw new Error(response.data.message || "Failed to set exchange rate")
      } catch (error) {
        this.error = error.response?.data?.message || error.message || "Failed to set exchange rate"
        console.error("Error setting manual exchange rate:", error)
        throw error
      } finally {
        this.isLoading = false
      }
    },
  }
})
